import { useMemo, useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Camera, ChevronDown, Lock, MoreVertical, Send, SlidersHorizontal, X } from 'lucide-react';
import api from '../lib/axios';
import { euro, dateFmt } from '../lib/format';
import SearchableSelect from '../components/SearchableSelect';
import { useModuleVisibility } from '../lib/useModuleVisibility';

// Camera capture (scan) is only offered on touch/mobile devices.
const IS_MOBILE =
  typeof navigator !== 'undefined' && /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

type LineForm = { accountNumber: string; label: string; debit: string; credit: string };
type Mode = 'simple' | 'advanced';
type Sens = 'income' | 'expense';
type PaymentMethod = 'CASH' | 'CHECK' | 'TRANSFER' | 'CARD' | 'DIRECT_DEBIT';

// Each payment method resolves to a journal type and the treasury account moved.
const METHOD_MAP: Record<PaymentMethod, { journalType: string; account: string }> = {
  CASH: { journalType: 'CASH', account: '531' },
  CHECK: { journalType: 'BANK', account: '5112' },
  TRANSFER: { journalType: 'BANK', account: '512' },
  CARD: { journalType: 'BANK', account: '512' },
  DIRECT_DEBIT: { journalType: 'BANK', account: '512' },
};

const PAYMENT_METHODS: PaymentMethod[] = ['CASH', 'CHECK', 'TRANSFER', 'CARD', 'DIRECT_DEBIT'];

// Treasury-account kind a payment method draws from, or null when the money account
// is fixed (received cheques transit through 5112 regardless of the bank).
function treasuryKindOf(method: PaymentMethod): 'BANK' | 'CASH' | null {
  if (method === 'CASH') return 'CASH';
  if (method === 'CHECK') return null;
  return 'BANK';
}

const EMPTY_LINE: LineForm = { accountNumber: '', label: '', debit: '', credit: '' };

// Infer the payment method from an entry's treasury line: cash (53x) and cheque (511x)
// are unambiguous; a plain bank line (512) stays undefined (shown as the "Banque" fallback).
function inferMethodFromLines(lines: LineForm[]): PaymentMethod | null {
  const money = lines.find((l) => l.accountNumber.startsWith('5'));
  const acc = money?.accountNumber ?? '';
  if (acc.startsWith('53')) return 'CASH';
  if (acc.startsWith('511')) return 'CHECK';
  return null;
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

// Reconstruct simple-form fields from a two-line entry (one treasury line facing
// one category line). Returns null when the entry is too complex for simple mode.
function toSimpleEntry(
  lines: LineForm[],
): { sens: Sens; payment: PaymentMethod; category: string; amount: string; treasury: string } | null {
  if (lines.length !== 2) return null;
  const money = lines.filter((l) => l.accountNumber.startsWith('5'));
  if (money.length !== 1) return null;
  const moneyLine = money[0];
  const categoryLine = lines.find((l) => l !== moneyLine);
  if (!categoryLine?.accountNumber) return null;
  const sens: Sens = Number(moneyLine.debit) > 0 ? 'income' : 'expense';
  const amount = sens === 'income' ? moneyLine.debit : moneyLine.credit;
  if (!(Number(amount) > 0)) return null;
  let payment: PaymentMethod = 'TRANSFER';
  if (moneyLine.accountNumber.startsWith('53')) payment = 'CASH';
  else if (moneyLine.accountNumber.startsWith('511')) payment = 'CHECK';
  return { sens, payment, category: categoryLine.accountNumber, amount, treasury: moneyLine.accountNumber };
}

// Derive the payment channel from the treasury line (class-5 account) of the entry.
function payKindOf(lines?: { accountNumber?: string }[]): 'CASH' | 'CHECK' | 'BANK' | null {
  const money = (lines ?? []).find((l) => l.accountNumber?.startsWith('5'));
  const acc = money?.accountNumber;
  if (!acc) return null;
  if (acc.startsWith('53')) return 'CASH';
  if (acc.startsWith('511')) return 'CHECK';
  if (acc.startsWith('512')) return 'BANK';
  return null;
}

// Small badge showing how the entry was settled (cash, cheque, transfer, card, direct debit).
const METHOD_STYLE: Record<string, string> = {
  CASH: 'bg-gold-tint text-gold',
  CHECK: 'bg-brand/10 text-brand',
  TRANSFER: 'bg-income-tint text-income',
  CARD: 'bg-surface-raised text-ink-soft',
  DIRECT_DEBIT: 'bg-expense-tint text-expense',
};

function PaymentBadge({
  method,
  lines,
}: Readonly<{ method?: string | null; lines?: { accountNumber?: string }[] }>) {
  const { t } = useTranslation();
  // Prefer the explicitly stored method; fall back to inferring from the treasury account for old entries.
  const resolved = method || payKindOf(lines);
  if (!resolved) return <span className="text-ink-muted">-</span>;
  const style = METHOD_STYLE[resolved] ?? 'bg-surface-raised text-ink-soft';
  const label = resolved === 'BANK' ? t('entries.bank') : t(`paymentMethod.${resolved}`);
  return (
    <span className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium ${style}`}>{label}</span>
  );
}

// Lightweight kebab (⋮) dropdown. Uses fixed positioning so it is never clipped by the
// scrollable table container; a transparent backdrop closes it on outside click.
function KebabMenu({
  ariaLabel,
  children,
}: Readonly<{ ariaLabel: string; children: (close: () => void) => JSX.Element }>) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const close = () => setOpen(false);
  const toggle = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 4, left: r.right });
    setOpen((v) => !v);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        className="btn btn-ghost !px-2 !py-1"
      >
        <MoreVertical size={16} />
      </button>
      {open && (
        <>
          <button type="button" aria-hidden className="fixed inset-0 z-40 cursor-default" onClick={close} />
          <div
            role="menu"
            style={{ position: 'fixed', top: pos.top, left: pos.left, transform: 'translateX(-100%)' }}
            className="z-50 min-w-[10rem] overflow-hidden rounded-xl border border-line bg-surface p-1 shadow-lg"
          >
            {children(close)}
          </div>
        </>
      )}
    </>
  );
}

// Nature (category) accounts of an entry: the non-treasury lines (class 6/7), deduplicated.
function natureAccountsOf(
  lines: { accountNumber?: string; label?: string }[] | undefined,
  labels: Map<string, string>,
): { number: string; label: string }[] {
  const seen = new Set<string>();
  const out: { number: string; label: string }[] = [];
  for (const l of lines ?? []) {
    const n = l.accountNumber ? String(l.accountNumber) : '';
    if (!n || n.startsWith('5') || seen.has(n)) continue;
    seen.add(n);
    out.push({ number: n, label: labels.get(n) ?? l.label ?? '' });
  }
  return out;
}

export default function Entries() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { isHidden } = useModuleVisibility();
  const contactsHidden = isHidden('contacts');
  const campaignsHidden = isHidden('campaigns');
  const [showForm, setShowForm] = useState(false);
  const [mode, setMode] = useState<Mode>('simple');

  // Shared
  const [entryDate, setEntryDate] = useState(today());
  const [label, setLabel] = useState('');
  const [reference, setReference] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Simple mode
  const [sens, setSens] = useState<Sens>('income');
  const [payment, setPayment] = useState<PaymentMethod>('TRANSFER');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('');
  const [treasuryAccount, setTreasuryAccount] = useState('');
  const [chequeNumber, setChequeNumber] = useState('');
  const [chequeDrawer, setChequeDrawer] = useState('');
  const [chequeBank, setChequeBank] = useState('');
  const [scanningCheque, setScanningCheque] = useState(false);
  const [scanningInvoice, setScanningInvoice] = useState(false);

  // Optional associations (contact / campaign / note / document)
  const [contactId, setContactId] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [note, setNote] = useState('');
  const [docFile, setDocFile] = useState<File | null>(null);
  const [docKind, setDocKind] = useState<'INVOICE' | 'DOCUMENT' | 'RECEIPT'>('DOCUMENT');

  // Advanced mode
  const [journalId, setJournalId] = useState('');
  const [lines, setLines] = useState<LineForm[]>([{ ...EMPTY_LINE }, { ...EMPTY_LINE }]);
  const [editingId, setEditingId] = useState<number | null>(null);

  // Entry list filters (all optional, sent as query params).
  const [fFrom, setFFrom] = useState('');
  const [fTo, setFTo] = useState('');
  const [fContact, setFContact] = useState('');
  const [fMin, setFMin] = useState('');
  const [fMax, setFMax] = useState('');
  const [fFlow, setFFlow] = useState<'' | 'INCOME' | 'EXPENSE'>('');
  const [fStatus, setFStatus] = useState<'' | 'DRAFT' | 'POSTED' | 'RECONCILED' | 'MATCHED'>('');
  const [fAccount, setFAccount] = useState('');
  const [fBank, setFBank] = useState('');
  const [fQuery, setFQuery] = useState('');

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [detailEntry, setDetailEntry] = useState<any | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  const filterParams = useMemo(
    () => ({
      page,
      size: PAGE_SIZE,
      ...(fFrom ? { startDate: fFrom } : {}),
      ...(fTo ? { endDate: fTo } : {}),
      ...(fContact ? { contactId: fContact } : {}),
      ...(fMin ? { minAmount: fMin } : {}),
      ...(fMax ? { maxAmount: fMax } : {}),
      ...(fFlow ? { flow: fFlow } : {}),
      ...(fStatus ? { status: fStatus } : {}),
      ...(fAccount ? { accountNumber: fAccount } : {}),
      ...(fBank ? { bankAccount: fBank } : {}),
      ...(fQuery.trim() ? { query: fQuery.trim() } : {}),
    }),
    [page, fFrom, fTo, fContact, fMin, fMax, fFlow, fStatus, fAccount, fBank, fQuery],
  );

  const hasFilters = Boolean(fFrom || fTo || fContact || fMin || fMax || fFlow || fStatus || fAccount || fBank || fQuery.trim());

  const resetFilters = () => {
    setFFrom('');
    setFTo('');
    setFContact('');
    setFMin('');
    setFMax('');
    setFFlow('');
    setFStatus('');
    setFAccount('');
    setFBank('');
    setFQuery('');
    setPage(0);
  };

  const { data, isLoading } = useQuery({
    queryKey: ['entries', filterParams],
    queryFn: () => api.get('/entries', { params: filterParams }).then((r) => r.data),
  });

  // Any filter change returns to the first page so results are never hidden on a later page.
  useEffect(() => {
    setPage(0);
  }, [fFrom, fTo, fContact, fMin, fMax, fFlow, fStatus, fAccount, fBank, fQuery]);

  const journals = useQuery({
    queryKey: ['journals'],
    queryFn: () => api.get('/journals').then((r) => r.data),
  });

  const accounts = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get('/accounts').then((r) => r.data),
  });

  const contacts = useQuery({
    queryKey: ['contacts'],
    queryFn: () => api.get('/contacts').then((r) => r.data),
    enabled: !contactsHidden,
  });

  const campaigns = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => api.get('/campaigns').then((r) => r.data),
    enabled: !campaignsHidden,
  });

  const treasury = useQuery({
    queryKey: ['treasury-accounts'],
    queryFn: () => api.get('/treasury-accounts').then((r) => r.data),
  });

  // Bank/cash sub-accounts offered in simple mode for the current payment method.
  const treasuryKind = treasuryKindOf(payment);
  const treasuryOptions = useMemo(
    () =>
      (treasury.data ?? [])
        .filter((a: any) => a.kind === treasuryKind)
        .map((a: any) => ({ value: a.accountNumber, label: a.name })),
    [treasury.data, treasuryKind],
  );
  const defaultTreasury = useMemo(() => {
    const list = (treasury.data ?? []).filter((a: any) => a.kind === treasuryKind);
    const def = list.find((a: any) => a.defaultAccount) ?? list[0];
    return def?.accountNumber ?? '';
  }, [treasury.data, treasuryKind]);

  // Keep the selected treasury account valid for the active method, defaulting to the
  // one flagged as default in settings.
  useEffect(() => {
    if (!treasuryKind) {
      setTreasuryAccount('');
      return;
    }
    setTreasuryAccount((cur) =>
      treasuryOptions.some((o: { value: string }) => o.value === cur) ? cur : defaultTreasury,
    );
  }, [treasuryKind, defaultTreasury, treasuryOptions]);

  // Categories shown in simple mode: income = class 7, expense = class 6 (detail accounts).
  const categories = useMemo(() => {
    const cls = sens === 'income' ? 7 : 6;
    return (accounts.data ?? []).filter(
      (a: any) => a.accountClass === cls && a.nature === 'DETAIL' && a.active,
    );
  }, [accounts.data, sens]);

  const categoryOptions = useMemo(() => {
    const opts = categories.map((a: any) => ({ value: a.number, label: `${a.number} - ${a.label}` }));
    // Keep the currently selected category visible even if it is a parent/inactive
    // account excluded from the detail list, so editing an entry prefills it.
    if (category && !opts.some((o: { value: string }) => o.value === category)) {
      const acc = (accounts.data ?? []).find((a: any) => String(a.number) === String(category));
      opts.unshift({ value: category, label: acc ? `${acc.number} - ${acc.label}` : category });
    }
    return opts;
  }, [categories, category, accounts.data]);

  // Account number → label, to show a compact nature-account badge on each entry row.
  const accountLabels = useMemo(() => {
    const m = new Map<string, string>();
    (accounts.data ?? []).forEach((a: any) => m.set(String(a.number), a.label));
    return m;
  }, [accounts.data]);

  // Every active account, used by the entries-list "accounting account" filter.
  const filterAccountOptions = useMemo(
    () =>
      (accounts.data ?? [])
        .filter((a: any) => a.active)
        .map((a: any) => ({ value: a.number, label: `${a.number} - ${a.label}` })),
    [accounts.data],
  );

  // Bank sub-accounts (class 512) used by the entries-list "bank account" filter.
  const filterBankOptions = useMemo(
    () =>
      (accounts.data ?? [])
        .filter((a: any) => a.active && String(a.number).startsWith('512'))
        .map((a: any) => ({ value: a.number, label: `${a.number} - ${a.label}` })),
    [accounts.data],
  );

  // Resolve the journal matching the payment method (bank or cash).
  const findJournal = (method: PaymentMethod) => {
    const list = journals.data ?? [];
    const wantType = METHOD_MAP[method].journalType;
    const wantCode = wantType === 'CASH' ? 'CA' : 'BQ';
    return (
      list.find((j: any) => j.type === wantType) ??
      list.find((j: any) => j.code === wantCode) ??
      null
    );
  };

  const moneyAccount = (method: PaymentMethod, journal: any) => {
    // A chosen bank account / cash register wins over the generic 512/531 fallback.
    if (treasuryKindOf(method) && treasuryAccount) {
      return treasuryAccount;
    }
    if (method === 'CASH') {
      return journal?.counterpartAccount || METHOD_MAP.CASH.account;
    }
    return METHOD_MAP[method].account;
  };

  const totals = useMemo(() => {
    const debit = lines.reduce((s, l) => s + (Number(l.debit) || 0), 0);
    const credit = lines.reduce((s, l) => s + (Number(l.credit) || 0), 0);
    return { debit, credit, balanced: Math.abs(debit - credit) < 0.005 && debit > 0 };
  }, [lines]);

  const create = useMutation({
    mutationFn: (body: any) => api.post('/entries', body).then((r) => r.data),
    onSuccess: async (entry: any) => {
      if (entry?.id) {
        await uploadDoc(entry.id);
        if (mode === 'simple' && payment === 'CHECK' && sens === 'income') {
          await registerCheque(entry.id);
        }
      }
      qc.invalidateQueries({ queryKey: ['entries'] });
      resetForm();
    },
    onError: (e: any) => {
      setError(e?.response?.data?.message ?? t('entries.saveError'));
    },
  });

  const update = useMutation({
    mutationFn: (body: any) => api.put(`/entries/${editingId}`, body).then((r) => r.data),
    onSuccess: async (entry: any) => {
      if (entry?.id) await uploadDoc(entry.id);
      qc.invalidateQueries({ queryKey: ['entries'] });
      resetForm();
    },
    onError: (e: any) => {
      setError(e?.response?.data?.message ?? t('entries.saveError'));
    },
  });

  const post = useMutation({
    mutationFn: (id: number) => api.post(`/entries/${id}/validate`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['entries'] }),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/entries/${id}`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['entries'] }),
  });

  const bulkRemove = useMutation({
    mutationFn: (ids: number[]) => api.post('/entries/bulk-delete', ids).then((r) => r.data),
    onSuccess: () => {
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ['entries'] });
    },
  });

  const bulkValidate = useMutation({
    mutationFn: (ids: number[]) => api.post('/entries/bulk-validate', ids).then((r) => r.data),
    onSuccess: () => {
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ['entries'] });
    },
  });

  const resetForm = () => {
    setShowForm(false);
    setMode('simple');
    setEditingId(null);
    setEntryDate(today());
    setLabel('');
    setReference('');
    setSens('income');
    setPayment('TRANSFER');
    setAmount('');
    setCategory('');
    setTreasuryAccount('');
    setChequeNumber('');
    setChequeDrawer('');
    setChequeBank('');
    setJournalId('');
    setLines([{ ...EMPTY_LINE }, { ...EMPTY_LINE }]);
    setContactId('');
    setCampaignId('');
    setNote('');
    setDocFile(null);
    setDocKind('DOCUMENT');
    setError(null);
  };

  const editEntry = (e: any) => {
    setError(null);
    setShowForm(true);
    setEditingId(e.id);
    setJournalId(e.journalId != null ? String(e.journalId) : '');
    setEntryDate(e.entryDate);
    setLabel(e.label ?? '');
    setReference(e.reference ?? '');
    setContactId(e.contactId != null ? String(e.contactId) : '');
    setCampaignId(e.campaignId != null ? String(e.campaignId) : '');
    setNote(e.note ?? '');
    setDocFile(null);
    setDocKind('DOCUMENT');
    const editLines: LineForm[] = (e.lines ?? []).map((l: any) => ({
      accountNumber: l.accountNumber ?? '',
      label: l.label ?? '',
      debit: l.debit ? String(l.debit) : '',
      credit: l.credit ? String(l.credit) : '',
    }));
    setLines(editLines.length >= 2 ? editLines : [{ ...EMPTY_LINE }, { ...EMPTY_LINE }]);

    // Prefer the simple form; fall back to advanced when the entry cannot be
    // represented as a single treasury line facing one category line.
    const simple = toSimpleEntry(editLines);
    if (simple) {
      setMode('simple');
      setSens(simple.sens);
      setPayment((e.paymentMethod as PaymentMethod) || simple.payment);
      setCategory(simple.category);
      setAmount(simple.amount);
      setTreasuryAccount(simple.treasury);
    } else {
      setMode('advanced');
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const updateLine = (idx: number, patch: Partial<LineForm>) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  const uploadDoc = async (entryId: number) => {
    if (!docFile) return;
    const form = new FormData();
    form.append('file', docFile);
    try {
      await api.post(`/entries/${entryId}/attachments?kind=${docKind}`, form);
    } catch {
      // A failed upload must not break the entry creation flow.
    }
  };

  // Best-effort: track a received cheque in treasury once its entry exists.
  const registerCheque = async (entryId: number) => {
    if (!chequeNumber || !(Number(amount) > 0)) return;
    try {
      await api.post('/checks', {
        number: chequeNumber,
        issuingBank: chequeBank || null,
        drawer: chequeDrawer || null,
        partyLabel: chequeDrawer || null,
        amount: Number(amount),
        receivedDate: entryDate,
        purpose: label || null,
        receiptEntryId: entryId,
      });
    } catch {
      // Cheque tracking is best-effort; it must not break entry creation.
    }
  };

  const runScan = async (
    path: '/scan/cheque' | '/scan/invoice',
    file: File,
    apply: (data: any) => void,
    setBusy: (v: boolean) => void,
  ) => {
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await api.post(path, form);
      apply(r.data ?? {});
    } catch (e: any) {
      setError(e?.response?.data?.message ?? t('entries.scanError'));
    } finally {
      setBusy(false);
    }
  };

  const onScanCheque = (file: File) =>
    runScan(
      '/scan/cheque',
      file,
      (d) => {
        if (d.number) setChequeNumber(d.number);
        if (d.drawer) setChequeDrawer(d.drawer);
        if (d.bank) setChequeBank(d.bank);
        if (d.amount) setAmount(String(d.amount));
        if (d.date) setEntryDate(d.date);
      },
      setScanningCheque,
    );

  const onScanInvoice = (file: File) => {
    setDocFile(file);
    setDocKind('INVOICE');
    return runScan(
      '/scan/invoice',
      file,
      (d) => {
        if (d.amount) setAmount(String(d.amount));
        if (d.label) setLabel((prev) => prev || d.label);
        if (d.date) setEntryDate(d.date);
      },
      setScanningInvoice,
    );
  };

  const submitSimple = () => {
    setError(null);
    const amt = Number(amount);
    if (!category || !label || !(amt > 0)) {
      setError(t('entries.missingFields'));
      return;
    }
    const journal = findJournal(payment);
    if (!journal) {
      setError(t('entries.saveError'));
      return;
    }
    const money = moneyAccount(payment, journal);
    // Auto double-entry: income = debit money / credit category; expense = the opposite.
    const entryLines =
      sens === 'income'
        ? [
            { accountNumber: money, label, debit: amt, credit: null },
            { accountNumber: category, label, debit: null, credit: amt },
          ]
        : [
            { accountNumber: category, label, debit: amt, credit: null },
            { accountNumber: money, label, debit: null, credit: amt },
          ];
    const isCheque = payment === 'CHECK';
    const chequeRef = isCheque && chequeNumber ? chequeNumber : reference;
    const chequeNote =
      isCheque && (chequeDrawer || chequeBank)
        ? [note, [chequeDrawer, chequeBank].filter(Boolean).join(' · ')]
            .filter(Boolean)
            .join(' - ')
        : note;
    const body = {
      journalId: editingId != null && journalId ? Number(journalId) : Number(journal.id),
      entryDate,
      label,
      reference: chequeRef || null,
      contactId: contactId ? Number(contactId) : null,
      campaignId: campaignId ? Number(campaignId) : null,
      note: chequeNote || null,
      paymentMethod: payment,
      lines: entryLines,
    };
    if (editingId != null) {
      update.mutate(body);
    } else {
      create.mutate(body);
    }
  };

  const submitAdvanced = () => {
    setError(null);
    if (!journalId || !label || !totals.balanced) {
      setError(t('entries.unbalanced'));
      return;
    }
    const body = {
      journalId: Number(journalId),
      entryDate,
      label,
      reference: reference || null,
      contactId: contactId ? Number(contactId) : null,
      campaignId: campaignId ? Number(campaignId) : null,
      note: note || null,
      // Cash / cheque are unambiguous from the treasury account; a plain 512 line stays
      // undefined so the list shows the generic "Banque" fallback instead of guessing.
      paymentMethod: inferMethodFromLines(lines),
      lines: lines
        .filter((l) => l.accountNumber && (Number(l.debit) || Number(l.credit)))
        .map((l) => ({
          accountNumber: l.accountNumber,
          label: l.label || null,
          debit: Number(l.debit) || null,
          credit: Number(l.credit) || null,
        })),
    };
    if (editingId != null) {
      update.mutate(body);
    } else {
      create.mutate(body);
    }
  };

  const saving = create.isPending || update.isPending;

  const rows = data?.content ?? data ?? [];
  const totalPages: number = data?.totalPages ?? 1;
  const totalElements: number = data?.totalElements ?? rows.length;
  // Draft rows of the current page that are selected: eligible for bulk validation.
  const draftSelectedIds: number[] = rows
    .filter((e: any) => selected.has(e.id) && e.status === 'DRAFT' && !e.system)
    .map((e: any) => e.id as number);

  const allSelectableChecked =
    rows.some((e: any) => !e.system) && rows.every((e: any) => e.system || selected.has(e.id));
  const toggleAll = (checked: boolean) =>
    setSelected(
      checked ? new Set(rows.filter((e: any) => !e.system).map((e: any) => e.id as number)) : new Set(),
    );
  const toggleOne = (id: number, checked: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });

  const renderAmount = (e: any) =>
    e.flow === 'INCOME' ? (
      <span className="font-medium text-income">+{euro(e.totalDebit)}</span>
    ) : e.flow === 'EXPENSE' ? (
      <span className="font-medium text-expense">−{euro(e.totalDebit)}</span>
    ) : (
      <span className="text-ink-soft">{euro(e.totalDebit)}</span>
    );

  const renderNatureChip = (e: any, className = 'mt-1 max-w-[18rem]') => {
    const nat = natureAccountsOf(e.lines, accountLabels);
    if (nat.length === 0) return null;
    const first = nat[0];
    const extra = nat.length - 1;
    return (
      <span
        className={`flex w-fit items-center gap-1 rounded-md bg-surface-raised px-1.5 py-0.5 text-[11px] font-medium ${className}`}
        title={nat.map((a) => `${a.number} - ${a.label}`).join('\n')}
      >
        <span className="tnum text-ink">{first.number}</span>
        {first.label && <span className="truncate text-ink-muted">{first.label}</span>}
        {extra > 0 && <span className="shrink-0 text-ink-muted">+{extra}</span>}
      </span>
    );
  };

  const renderOriginBadges = (e: any) => (
    <>
      {e.system && (
        <span
          className="inline-flex items-center gap-1 rounded-full bg-gold-tint px-1.5 py-0.5 text-[11px] font-medium text-gold"
          title={t('entries.openingLocked')}
        >
          <Lock size={11} />
          {t('entries.opening')}
        </span>
      )}
      {e.source === 'TELEGRAM' && (
        <span
          className="inline-flex items-center gap-1 rounded-full bg-brand/10 px-1.5 py-0.5 text-[11px] font-medium text-brand"
          title={t('entries.fromTelegram')}
        >
          <Send size={11} />
          Telegram
        </span>
      )}
    </>
  );

  const renderActions = (e: any) => {
    if (e.system) {
      return (
        <div className="flex items-center justify-end gap-1 text-xs text-ink-muted">
          <Lock size={13} />
          {t('entries.openingLocked')}
        </div>
      );
    }
    const draft = e.status === 'DRAFT';
    return (
      <div className="flex justify-end">
        <KebabMenu ariaLabel={t('field.actions')}>
          {(close) => (
            <>
              <button
                role="menuitem"
                onClick={() => {
                  close();
                  editEntry(e);
                }}
                className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm hover:bg-surface-raised"
                title={draft ? undefined : t('entries.editPostedHint')}
              >
                {draft ? t('common.edit') : t('entries.editPosted')}
              </button>
              {draft && (
                <button
                  role="menuitem"
                  onClick={() => {
                    close();
                    post.mutate(e.id);
                  }}
                  className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-income hover:bg-surface-raised"
                >
                  {t('entries.post')}
                </button>
              )}
              <button
                role="menuitem"
                onClick={() => {
                  close();
                  if (window.confirm(t('entries.confirmDelete'))) remove.mutate(e.id);
                }}
                className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-expense hover:bg-surface-raised"
              >
                {t('common.delete')}
              </button>
            </>
          )}
        </KebabMenu>
      </div>
    );
  };

  return (
    <div>
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <p className="eyebrow">{t('nav.entries')}</p>
          <h1 className="page-title mt-1">{t('entries.title')}</h1>
        </div>
        <button
          onClick={() => (showForm ? resetForm() : setShowForm(true))}
          className={showForm ? 'btn btn-secondary' : 'btn btn-primary'}
        >
          {showForm ? t('entries.cancel') : t('entries.new')}
        </button>
      </div>

      {showForm && (
        <div className="reveal card mb-6 p-4 sm:p-6">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold">
              {editingId != null ? t('entries.edit') : t('entries.new')}
            </h2>
            <button
              onClick={() => setMode((m) => (m === 'simple' ? 'advanced' : 'simple'))}
              className="text-sm font-medium text-brand hover:text-brand-strong"
            >
              {mode === 'simple' ? t('entries.advancedMode') : t('entries.simpleMode')}
            </button>
          </div>

          {mode === 'simple' ? (
            <div>
              <div className="mb-5 inline-flex gap-1 rounded-xl border border-line bg-surface-raised p-1">
                <button
                  onClick={() => {
                    setSens('income');
                    setCategory('');
                  }}
                  className={`rounded-lg px-4 py-1.5 text-sm font-medium transition-colors ${
                    sens === 'income' ? 'bg-income-tint text-income' : 'text-ink-muted hover:text-ink'
                  }`}
                >
                  {t('entries.income')}
                </button>
                <button
                  onClick={() => {
                    setSens('expense');
                    setCategory('');
                  }}
                  className={`rounded-lg px-4 py-1.5 text-sm font-medium transition-colors ${
                    sens === 'expense' ? 'bg-expense-tint text-expense' : 'text-ink-muted hover:text-ink'
                  }`}
                >
                  {t('entries.expense')}
                </button>
              </div>

              <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="field-label">{t('entries.category')}</label>
                  <SearchableSelect
                    value={category}
                    onChange={setCategory}
                    options={categoryOptions}
                    placeholder={t('entries.selectCategory')}
                    searchPlaceholder={t('entries.searchCategory')}
                    emptyText={t('entries.noCategoryMatch')}
                  />
                </div>
                <div>
                  <label className="field-label">{t('field.amount')}</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="input text-right"
                    placeholder="0,00"
                  />
                </div>
                <div>
                  <label className="field-label">{t('field.label')}</label>
                  <input
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    className="input"
                    placeholder={t('entries.labelPlaceholder')}
                  />
                </div>
                <div>
                  <label className="field-label">{t('entries.method')}</label>
                  <select
                    value={payment}
                    onChange={(e) => setPayment(e.target.value as PaymentMethod)}
                    className="select"
                  >
                    {PAYMENT_METHODS.map((m) => (
                      <option key={m} value={m}>
                        {t(`paymentMethod.${m}`)}
                      </option>
                    ))}
                  </select>
                </div>
                {treasuryKind && treasuryOptions.length > 0 && (
                  <div>
                    <label className="field-label">
                      {treasuryKind === 'CASH' ? t('entries.cashRegister') : t('entries.bankAccount')}
                    </label>
                    <select
                      value={treasuryAccount}
                      onChange={(e) => setTreasuryAccount(e.target.value)}
                      className="select"
                    >
                      {treasuryOptions.map((o: { value: string; label: string }) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {payment === 'CHECK' && (
                  <div className="rounded-xl border border-line bg-surface-raised p-4 md:col-span-2">
                    <div className="mb-3 flex items-center justify-between">
                      <span className="text-sm font-medium text-ink">
                        {t('entries.chequeDetails')}
                      </span>
                      {IS_MOBILE && (
                        <label className="btn btn-secondary !py-1.5 cursor-pointer">
                          <Camera size={16} />
                          {scanningCheque ? t('entries.scanning') : t('entries.scanCheque')}
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) onScanCheque(f);
                              e.target.value = '';
                            }}
                          />
                        </label>
                      )}
                    </div>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                      <div>
                        <label className="field-label">{t('entries.chequeNumber')}</label>
                        <input
                          value={chequeNumber}
                          onChange={(e) => setChequeNumber(e.target.value)}
                          className="input"
                        />
                      </div>
                      <div>
                        <label className="field-label">{t('entries.chequeDrawer')}</label>
                        <input
                          value={chequeDrawer}
                          onChange={(e) => setChequeDrawer(e.target.value)}
                          className="input"
                          placeholder={t('entries.chequeDrawerPlaceholder')}
                        />
                      </div>
                      <div>
                        <label className="field-label">{t('entries.chequeBank')}</label>
                        <input
                          value={chequeBank}
                          onChange={(e) => setChequeBank(e.target.value)}
                          className="input"
                        />
                      </div>
                    </div>
                  </div>
                )}
                <div>
                  <label className="field-label">{t('field.date')}</label>
                  <input
                    type="date"
                    value={entryDate}
                    onChange={(e) => setEntryDate(e.target.value)}
                    className="input"
                  />
                </div>
                <div>
                  <label className="field-label">{t('field.reference')}</label>
                  <input
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    className="input"
                  />
                </div>
              </div>

              <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                {!contactsHidden && (
                <div>
                  <label className="field-label">{t('entries.contact')}</label>
                  <select
                    value={contactId}
                    onChange={(e) => setContactId(e.target.value)}
                    className="select"
                  >
                    <option value="">{t('entries.noContact')}</option>
                    {(contacts.data ?? []).map((c: any) => (
                      <option key={c.id} value={c.id}>
                        {c.displayName}
                      </option>
                    ))}
                  </select>
                </div>
                )}
                {!campaignsHidden && (
                <div>
                  <label className="field-label">{t('entries.campaign')}</label>
                  <select
                    value={campaignId}
                    onChange={(e) => setCampaignId(e.target.value)}
                    className="select"
                  >
                    <option value="">{t('entries.noCampaign')}</option>
                    {(campaigns.data ?? []).map((c: any) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
                )}
                <div className="md:col-span-2">
                  <label className="field-label">{t('entries.note')}</label>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    className="input"
                    rows={2}
                    placeholder={t('entries.notePlaceholder')}
                  />
                </div>
                <div>
                  <label className="field-label">{t('entries.document')}</label>
                  <input
                    type="file"
                    onChange={(e) => setDocFile(e.target.files?.[0] ?? null)}
                    className="input"
                  />
                  {IS_MOBILE && (
                    <label className="btn btn-secondary mt-2 !py-1.5 cursor-pointer">
                      <Camera size={16} />
                      {scanningInvoice ? t('entries.scanning') : t('entries.scanInvoice')}
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) onScanInvoice(f);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  )}
                  {docFile && <p className="mt-1 text-xs text-ink-muted">{docFile.name}</p>}
                </div>
                <div>
                  <label className="field-label">{t('entries.documentKind')}</label>
                  <select
                    value={docKind}
                    onChange={(e) =>
                      setDocKind(e.target.value as 'INVOICE' | 'DOCUMENT' | 'RECEIPT')
                    }
                    className="select"
                  >
                    <option value="DOCUMENT">{t('attachment.DOCUMENT')}</option>
                    <option value="INVOICE">{t('attachment.INVOICE')}</option>
                    <option value="RECEIPT">{t('attachment.RECEIPT')}</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex-1" />
                {error && <span className="text-sm font-medium text-expense">{error}</span>}
                <button
                  onClick={submitSimple}
                  disabled={saving}
                  className="btn btn-primary"
                >
                  {saving ? t('entries.saving') : t('entries.save')}
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div className="mb-5 grid grid-cols-1 gap-4 md:grid-cols-4">
                <div>
                  <label className="field-label">{t('field.journal')}</label>
                  <select
                    value={journalId}
                    onChange={(e) => setJournalId(e.target.value)}
                    className="select"
                  >
                    <option value="">{t('common.none')}</option>
                    {(journals.data ?? []).map((j: any) => (
                      <option key={j.id} value={j.id}>
                        {j.code} · {j.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="field-label">{t('field.date')}</label>
                  <input
                    type="date"
                    value={entryDate}
                    onChange={(e) => setEntryDate(e.target.value)}
                    className="input"
                  />
                </div>
                <div>
                  <label className="field-label">{t('field.label')}</label>
                  <input
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    className="input"
                  />
                </div>
                <div>
                  <label className="field-label">{t('field.reference')}</label>
                  <input
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    className="input"
                  />
                </div>
              </div>

              <div className="overflow-x-auto rounded-xl border border-line">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('field.account')}</th>
                      <th>{t('field.label')}</th>
                      <th className="num">{t('field.debit')}</th>
                      <th className="num">{t('field.credit')}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l, idx) => (
                      <tr key={idx}>
                        <td data-label={t('field.account')} className="pr-2">
                          <input
                            list="account-options"
                            value={l.accountNumber}
                            onChange={(e) => updateLine(idx, { accountNumber: e.target.value })}
                            className="input tnum w-32"
                          />
                        </td>
                        <td data-label={t('field.label')} className="pr-2">
                          <input
                            value={l.label}
                            onChange={(e) => updateLine(idx, { label: e.target.value })}
                            className="input"
                          />
                        </td>
                        <td data-label={t('field.debit')} className="pr-2">
                          <input
                            type="number"
                            step="0.01"
                            value={l.debit}
                            onChange={(e) => updateLine(idx, { debit: e.target.value, credit: '' })}
                            className="input w-28 text-right"
                          />
                        </td>
                        <td data-label={t('field.credit')} className="pr-2">
                          <input
                            type="number"
                            step="0.01"
                            value={l.credit}
                            onChange={(e) => updateLine(idx, { credit: e.target.value, debit: '' })}
                            className="input w-28 text-right"
                          />
                        </td>
                        <td className="cell-actions text-right">
                          {lines.length > 2 && (
                            <button
                              onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))}
                              className="btn btn-ghost !px-2 text-expense"
                              title={t('entries.removeLine')}
                            >
                              ×
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="font-medium">
                      <td colSpan={2} className="num pr-2 text-ink-soft">
                        {t('field.balance')}
                      </td>
                      <td data-label={t('field.debit')} className="num">{euro(totals.debit)}</td>
                      <td data-label={t('field.credit')} className="num">{euro(totals.credit)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <datalist id="account-options">
                {(accounts.data ?? []).map((a: any) => (
                  <option key={a.number} value={a.number}>
                    {a.number} - {a.label}
                  </option>
                ))}
              </datalist>

              <div className="mt-4 flex items-center gap-3">
                <button
                  onClick={() => setLines((prev) => [...prev, { ...EMPTY_LINE }])}
                  className="text-sm font-medium text-brand hover:text-brand-strong"
                >
                  + {t('entries.addLine')}
                </button>
                <div className="flex-1" />
                {error && <span className="text-sm font-medium text-expense">{error}</span>}
                {!totals.balanced && !error && (
                  <span className="text-sm font-medium text-gold">{t('entries.unbalanced')}</span>
                )}
                <button
                  onClick={submitAdvanced}
                  disabled={saving || !totals.balanced}
                  className="btn btn-primary"
                >
                  {saving ? t('entries.saving') : t('entries.save')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="card mb-4 p-4">
        <button
          type="button"
          onClick={() => setFiltersOpen((o) => !o)}
          className="flex w-full items-center justify-between gap-2 text-left"
          aria-expanded={filtersOpen}
        >
          <span className="inline-flex items-center gap-2 text-sm font-medium text-ink-soft">
            <SlidersHorizontal size={16} />
            {t('entries.filters')}
            {hasFilters && (
              <span className="rounded-full bg-brand/10 px-2 py-0.5 text-xs font-medium text-brand">
                {t('entries.filtersActive')}
              </span>
            )}
          </span>
          <ChevronDown
            size={18}
            className={`shrink-0 text-ink-muted transition-transform ${filtersOpen ? 'rotate-180' : ''}`}
          />
        </button>
        {filtersOpen && (
          <div className="mt-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="field-label">{t('entries.filterFrom')}</label>
            <input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} className="input" />
          </div>
          <div>
            <label className="field-label">{t('entries.filterTo')}</label>
            <input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} className="input" />
          </div>
          <div>
            <label className="field-label">{t('entries.amountMin')}</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={fMin}
              onChange={(e) => setFMin(e.target.value)}
              className="input text-right"
              placeholder="0,00"
            />
          </div>
          <div>
            <label className="field-label">{t('entries.amountMax')}</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={fMax}
              onChange={(e) => setFMax(e.target.value)}
              className="input text-right"
              placeholder="0,00"
            />
          </div>
          {!contactsHidden && (
          <div>
            <label className="field-label">{t('entries.contact')}</label>
            <select value={fContact} onChange={(e) => setFContact(e.target.value)} className="select">
              <option value="">{t('entries.allContacts')}</option>
              {(contacts.data ?? []).map((c: any) => (
                <option key={c.id} value={c.id}>
                  {c.displayName}
                </option>
              ))}
            </select>
          </div>
          )}
          <div>
            <label className="field-label">{t('entries.flow')}</label>
            <select
              value={fFlow}
              onChange={(e) => setFFlow(e.target.value as '' | 'INCOME' | 'EXPENSE')}
              className="select"
            >
              <option value="">{t('entries.allFlows')}</option>
              <option value="INCOME">{t('entries.income')}</option>
              <option value="EXPENSE">{t('entries.expense')}</option>
            </select>
          </div>
          <div>
            <label className="field-label">{t('field.status')}</label>
            <select
              value={fStatus}
              onChange={(e) =>
                setFStatus(e.target.value as '' | 'DRAFT' | 'POSTED' | 'RECONCILED' | 'MATCHED')
              }
              className="select"
            >
              <option value="">{t('entries.allStatuses')}</option>
              <option value="DRAFT">{t('entries.status.DRAFT')}</option>
              <option value="POSTED">{t('entries.status.POSTED')}</option>
              <option value="RECONCILED">{t('entries.status.RECONCILED')}</option>
              <option value="MATCHED">{t('entries.status.MATCHED')}</option>
            </select>
          </div>
          <div>
            <label className="field-label">{t('entries.filterAccount')}</label>
            <SearchableSelect
              value={fAccount}
              onChange={setFAccount}
              options={filterAccountOptions}
              placeholder={t('entries.allAccounts')}
              searchPlaceholder={t('entries.searchAccount')}
              emptyText={t('entries.noAccountMatch')}
            />
          </div>
          <div>
            <label className="field-label">{t('entries.filterBank')}</label>
            <select value={fBank} onChange={(e) => setFBank(e.target.value)} className="select">
              <option value="">{t('entries.allBanks')}</option>
              {filterBankOptions.map((o: { value: string; label: string }) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="field-label">{t('entries.searchLabel')}</label>
            <input
              value={fQuery}
              onChange={(e) => setFQuery(e.target.value)}
              className="input"
              placeholder={t('entries.searchPlaceholder')}
            />
          </div>
            </div>
            {hasFilters && (
              <div className="mt-3 flex justify-end">
                <button onClick={resetFilters} className="btn btn-ghost !px-3 !py-1 text-sm">
                  {t('entries.resetFilters')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="card p-3">
        {selected.size > 0 && (
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-brand/5 px-3 py-2">
            <span className="text-sm text-ink-soft">
              {t('entries.selectedCount', { count: selected.size })}
            </span>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelected(new Set())}
                className="btn btn-ghost !px-3 !py-1 text-sm"
              >
                {t('common.cancel')}
              </button>
              {draftSelectedIds.length > 0 && (
                <button
                  onClick={() => bulkValidate.mutate(draftSelectedIds)}
                  disabled={bulkValidate.isPending}
                  className="btn btn-ghost !px-3 !py-1 text-sm text-income"
                >
                  {t('entries.validateSelection', { count: draftSelectedIds.length })}
                </button>
              )}
              <button
                onClick={() => {
                  if (window.confirm(t('entries.confirmBulkDelete', { count: selected.size })))
                    bulkRemove.mutate(Array.from(selected));
                }}
                className="btn btn-ghost !px-3 !py-1 text-sm text-expense"
              >
                {t('entries.deleteSelection')}
              </button>
            </div>
          </div>
        )}
        {/* Phones: a two-line list (label + amount, then date, status and method) - no sideways scroll. */}
        <div className="sm:hidden">
          {rows.some((e: any) => !e.system) && (
            <label className="flex items-center gap-2 px-1 pb-2 text-xs font-medium text-ink-muted">
              <input
                type="checkbox"
                checked={allSelectableChecked}
                onChange={(ev) => toggleAll(ev.target.checked)}
              />
              {t('entries.selectAll')}
            </label>
          )}
          {isLoading && (
            <p className="py-8 text-center text-sm text-ink-muted">{t('common.loading')}</p>
          )}
          {!isLoading && rows.length === 0 && (
            <p className="py-8 text-center text-sm text-ink-muted">{t('entries.noResults')}</p>
          )}
          <ul className="divide-y divide-line">
            {rows.map((e: any) => (
              <li key={e.id} className="flex items-start gap-3 px-1 py-3">
                <input
                  type="checkbox"
                  className="mt-1 shrink-0"
                  aria-label={t('entries.selectRow')}
                  disabled={!!e.system}
                  checked={selected.has(e.id)}
                  onChange={(ev) => toggleOne(e.id, ev.target.checked)}
                />
                <button
                  type="button"
                  onClick={() => setDetailEntry(e)}
                  className="min-w-0 flex-1 text-left"
                >
                  <span className="flex items-start justify-between gap-3">
                    <span className="line-clamp-2 min-w-0 font-medium leading-snug text-ink [overflow-wrap:anywhere]">
                      {e.label}
                    </span>
                    <span className="shrink-0 whitespace-nowrap text-sm tnum">{renderAmount(e)}</span>
                  </span>
                  <span className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-muted">
                    <span className="whitespace-nowrap">{dateFmt(e.entryDate)}</span>
                    <span aria-hidden>·</span>
                    <span className="tnum">{e.number}</span>
                    <span className={e.status === 'DRAFT' ? 'pill pill-draft' : 'pill pill-posted'}>
                      {t('entries.status.' + e.status) as string}
                    </span>
                    <PaymentBadge method={e.paymentMethod} lines={e.lines} />
                    {renderOriginBadges(e)}
                  </span>
                  {renderNatureChip(e, 'mt-1.5 max-w-full')}
                </button>
                {!e.system && <div className="-mr-1 shrink-0">{renderActions(e)}</div>}
              </li>
            ))}
          </ul>
        </div>

        <div className="hidden overflow-x-auto sm:block">
        <table className="table">
          <thead>
            <tr>
              <th className="w-8">
                <input
                  type="checkbox"
                  aria-label={t('entries.selectAll')}
                  checked={allSelectableChecked}
                  onChange={(ev) => toggleAll(ev.target.checked)}
                />
              </th>
              <th>{t('field.date')}</th>
              <th>{t('field.number')}</th>
              <th>{t('field.label')}</th>
              <th>{t('entries.method')}</th>
              <th className="num">{t('field.amount')}</th>
              <th>{t('field.status')}</th>
              <th className="text-right">{t('field.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={9} className="py-8 text-center text-ink-muted">
                  {t('common.loading')}
                </td>
              </tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={9} className="py-8 text-center text-ink-muted">
                  {t('entries.noResults')}
                </td>
              </tr>
            )}
            {rows.map((e: any) => {
              const draft = e.status === 'DRAFT';
              return (
                <tr key={e.id} className="cursor-pointer" onClick={() => setDetailEntry(e)}>
                  <td onClick={(ev) => ev.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={t('entries.selectRow')}
                      disabled={!!e.system}
                      checked={selected.has(e.id)}
                      onChange={(ev) => toggleOne(e.id, ev.target.checked)}
                    />
                  </td>
                  <td data-label={t('field.date')} className="whitespace-nowrap text-ink-soft">{dateFmt(e.entryDate)}</td>
                  <td data-label={t('field.number')} className="tnum text-ink-soft">{e.number}</td>
                  <td data-label={t('field.label')} className="font-medium">
                    <span className="inline-flex items-center gap-1.5">
                      {e.label}
                      {renderOriginBadges(e)}
                    </span>
                    {renderNatureChip(e)}
                  </td>
                  <td data-label={t('entries.method')}>
                    <PaymentBadge method={e.paymentMethod} lines={e.lines} />
                  </td>
                  <td data-label={t('field.amount')} className="num tnum">
                    {renderAmount(e)}
                  </td>
                  <td data-label={t('field.status')}>
                    <span className={draft ? 'pill pill-draft' : 'pill pill-posted'}>
                      {t('entries.status.' + e.status) as string}
                    </span>
                  </td>
                  <td className="cell-actions" onClick={(ev) => ev.stopPropagation()}>
                    {renderActions(e)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        {totalPages > 1 && (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-line/60 px-1 pt-3 text-sm text-ink-soft">
            <span>
              {t('entries.pageInfo', {
                page: page + 1,
                totalPages,
                total: totalElements,
              })}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page <= 0}
                className="btn btn-ghost !px-3 !py-1 text-sm disabled:opacity-40"
              >
                {t('common.previous')}
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="btn btn-ghost !px-3 !py-1 text-sm disabled:opacity-40"
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        )}
      </div>

      {detailEntry && (
        <div
          className="fixed inset-0 z-40 flex justify-end bg-black/40"
          onClick={() => setDetailEntry(null)}
        >
          <div
            className="reveal h-full w-full max-w-lg overflow-y-auto bg-surface p-5 shadow-xl sm:p-6"
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className="mb-4 flex items-start justify-between">
              <div>
                <p className="eyebrow">
                  {t('entries.detailTitle')} · {detailEntry.number}
                </p>
                <h2 className="page-title mt-1 text-xl">{detailEntry.label}</h2>
              </div>
              <button
                onClick={() => setDetailEntry(null)}
                className="btn btn-ghost !px-2 !py-2"
                aria-label={t('common.close')}
              >
                <X size={18} />
              </button>
            </div>

            <div className="mb-4 grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg bg-surface-raised p-3">
                <p className="text-xs text-ink-muted">{t('field.date')}</p>
                <p className="mt-0.5 font-medium text-ink">{dateFmt(detailEntry.entryDate)}</p>
              </div>
              <div className="rounded-lg bg-surface-raised p-3">
                <p className="text-xs text-ink-muted">{t('field.status')}</p>
                <p className="mt-0.5">
                  <span
                    className={detailEntry.status === 'DRAFT' ? 'pill pill-draft' : 'pill pill-posted'}
                  >
                    {t('entries.status.' + detailEntry.status) as string}
                  </span>
                </p>
              </div>
              <div className="rounded-lg bg-surface-raised p-3">
                <p className="text-xs text-ink-muted">{t('entries.method')}</p>
                <p className="mt-0.5">
                  <PaymentBadge method={detailEntry.paymentMethod} lines={detailEntry.lines} />
                </p>
              </div>
              {detailEntry.reference && (
                <div className="rounded-lg bg-surface-raised p-3">
                  <p className="text-xs text-ink-muted">{t('field.reference')}</p>
                  <p className="mt-0.5 font-medium text-ink">{detailEntry.reference}</p>
                </div>
              )}
            </div>

            {detailEntry.note && (
              <div className="mb-4 rounded-lg bg-surface-raised p-3 text-sm">
                <p className="text-xs text-ink-muted">{t('entries.note')}</p>
                <p className="mt-0.5 whitespace-pre-line text-ink-soft">{detailEntry.note}</p>
              </div>
            )}

            <p className="eyebrow mb-2">{t('field.lines')}</p>
            <div className="overflow-hidden rounded-lg border border-line">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('field.account')}</th>
                    <th className="num">{t('field.debit')}</th>
                    <th className="num">{t('field.credit')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(detailEntry.lines ?? []).map((l: any, i: number) => (
                    <tr key={i}>
                      <td data-label={t('field.account')}>
                        <span className="min-w-0">
                          <span className="tnum text-ink">{l.accountNumber}</span>
                          <span className="ml-1 text-ink-muted">
                            {accountLabels.get(String(l.accountNumber)) ?? l.label ?? ''}
                          </span>
                        </span>
                      </td>
                      <td data-label={t('field.debit')} className="num tnum">
                        {Number(l.debit) > 0 ? euro(l.debit) : '-'}
                      </td>
                      <td data-label={t('field.credit')} className="num tnum">
                        {Number(l.credit) > 0 ? euro(l.credit) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-5 flex items-center justify-end gap-3">
              {detailEntry.system ? (
                <span className="flex items-center gap-1.5 text-sm text-ink-muted">
                  <Lock size={14} /> {t('entries.openingLocked')}
                </span>
              ) : (
                <button
                  onClick={() => {
                    const target = detailEntry;
                    setDetailEntry(null);
                    editEntry(target);
                  }}
                  className="btn btn-primary"
                >
                  {detailEntry.status === 'DRAFT' ? t('entries.edit') : t('entries.editPosted')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
